import React, { ButtonHTMLAttributes } from 'react'
import styled, { css, StyledProps } from 'styled-components'

export type ChipColor = 'blue' | 'green' | 'darkgreen' | 'orange' | 'red' | 'gray'
export type ChipSize = 'medium' | 'small'
type ChipProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  color?: ChipColor
  bullet?: boolean
  active?: boolean
  value?: unknown
  className?: string
  size?: ChipSize
  onClick?(value: unknown, e: React.MouseEvent<HTMLButtonElement>): void
}
type StyledChipProps = StyledProps<{
  color: ChipColor
  clickable?: boolean
  bullet?: boolean
  active?: boolean
  size?: ChipSize
}>

const pickColor = (props: StyledChipProps) => {
  switch (props.color) {
    case 'blue':
      return props.theme.color.primary600
    case 'green':
      return props.theme.color.accentApprove600
    case 'darkgreen':
      return props.theme.color.accentApproving100
    case 'orange':
      return props.theme.color.accentNotify600
    case 'gray':
      return props.theme.color.backgroundPrimary
    case 'red':
      return props.theme.color.accentDestruct500
  }
}

const chipColorCss = (props: StyledChipProps) => {
  const selectedColor = pickColor(props)
  let backgroundColor
  let color
  let borderRadius
  let height
  const borderColor = (backgroundColor = color = selectedColor)

  let hoverBackgroundColor

  if (props.active) {
    color = props.color === 'gray' ? props.theme.color.onSurfaceMidEmphasys : props.theme.color.onPrimaryLight
    hoverBackgroundColor = `${backgroundColor}AA`
  } else {
    backgroundColor = props.theme.color.onPrimaryLight
    hoverBackgroundColor = `${selectedColor}22`
  }

  if (!props.clickable) {
    hoverBackgroundColor = backgroundColor
  }

  switch (props.size) {
    case 'small':
      borderRadius = '8px'
      height = '28px'
      break
    default:
      borderRadius = '4px'
      height = '32px'
  }

  return css`
    border-color: ${borderColor};
    background-color: ${backgroundColor};
    color: ${color};
    border-radius: ${borderRadius};
    height: ${height};
    &:hover {
      background-color: ${hoverBackgroundColor};
    }
    &:focus {
      box-shadow: 0 0 4px ${borderColor};
    }
  `
}

const ChipStyled = styled.button<StyledChipProps>`
  height: 32px;
  border-width: 1px;
  border-style: solid;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  box-sizing: border-box;
  border-radius: 4px;
  transition: 0.1s background-color ease-out;
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  outline: none;
  flex-shrink: 0;

  ${chipColorCss}
`

const BulletStyled = styled.div<StyledChipProps>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 8px;
  background-color: ${(props: StyledChipProps) => (props.active ? props.theme.color.onPrimaryLight : pickColor(props))};
`

export const Chip: React.FC<ChipProps> = ({
  children,
  color = 'blue',
  size = 'medium',
  active,
  bullet,
  value,
  onClick,
  className,
  tabIndex,
  ...rest
}) => {
  const clickable = !!onClick
  return (
    <ChipStyled
      clickable={clickable}
      color={color}
      active={active}
      onClick={e => onClick && onClick(value, e)}
      className={className}
      size={size}
      tabIndex={clickable ? tabIndex : -1}
      {...rest}
    >
      {bullet && <BulletStyled color={color} active={active} />}
      {children}
    </ChipStyled>
  )
}
